// Quadratic Equations Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const quadraticSamples = {
  fundamental: [
    {
      id: 'quad_fund_01',
      problem: "x² + 3x + 2 = 0",
      problemType: "quadratic",
      difficulty: "fundamental",
      description: "Basic quadratic with simple factoring",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "A polynomial equation of degree 2",
            value: "x² + 3x + 2 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring",
            name: "Factoring Method",
            type: "concepts",
            description: "Express as product of linear factors",
            value: "(x + a)(x + b) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "coefficients",
            name: "Coefficients",
            type: "values",
            description: "a=1, b=3, c=2",
            value: "a=1, b=3, c=2",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = -1",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -2",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "quadratic_factoring_formula",
            name: "Quadratic Factoring Formula",
            type: "formulas",
            description: "For x² + bx + c = 0, find factors that multiply to c and add to b",
            value: "x² + bx + c = (x + m)(x + n) where m × n = c and m + n = b",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_coefficients",
            name: "Identify Coefficients",
            type: "steps",
            description: "Extract a, b, c from the equation",
            value: "Step 1: a=1, b=3, c=2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_factors",
            name: "Find Factors",
            type: "steps",
            description: "Find two numbers that multiply to 2 and add to 3",
            value: "Step 2: Factors are 1 and 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "write_factored_form",
            name: "Write Factored Form",
            type: "steps",
            description: "Express as (x+1)(x+2) = 0",
            value: "Step 3: (x+1)(x+2) = 0",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "solve_linear_equations",
            name: "Solve Linear Equations",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 4: x+1=0 or x+2=0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "factoring", type: "solved by", strength: 9 },
          { from: "coefficients", to: "factoring", type: "used in", strength: 8 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_coefficients", type: "requires", strength: 8 },
          { from: "identify_coefficients", to: "find_factors", type: "enables", strength: 9 },
          { from: "find_factors", to: "write_factored_form", type: "leads to", strength: 9 },
          { from: "write_factored_form", to: "solve_linear_equations", type: "enables", strength: 10 },
          { from: "solve_linear_equations", to: "solution1", type: "gives", strength: 10 },
          { from: "solve_linear_equations", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_fund_02',
      problem: "x² - 4 = 0",
      problemType: "quadratic",
      difficulty: "fundamental",
      description: "Difference of squares",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Difference of squares form",
            value: "x² - 4 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "difference_squares",
            name: "Difference of Squares",
            type: "concepts",
            description: "a² - b² = (a+b)(a-b)",
            value: "x² - 2² = (x+2)(x-2)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 2",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -2",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_pattern",
            name: "Identify Pattern",
            type: "steps",
            description: "Recognize difference of squares pattern",
            value: "Step 1: x² - 4 = x² - 2²",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_formula",
            name: "Apply Difference of Squares",
            type: "steps",
            description: "Use a² - b² = (a+b)(a-b)",
            value: "Step 2: (x+2)(x-2) = 0",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_factors",
            name: "Solve Each Factor",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 3: x+2=0 or x-2=0",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "find_solutions",
            name: "Find Solutions",
            type: "steps",
            description: "Solve for x in each equation",
            value: "Step 4: x = -2 or x = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "difference_squares", type: "solved by", strength: 10 },
          { from: "difference_squares", to: "solution1", type: "gives", strength: 10 },
          { from: "difference_squares", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_pattern", type: "requires", strength: 9 },
          { from: "identify_pattern", to: "apply_formula", type: "enables", strength: 10 },
          { from: "apply_formula", to: "solve_factors", type: "leads to", strength: 10 },
          { from: "solve_factors", to: "find_solutions", type: "enables", strength: 10 },
          { from: "find_solutions", to: "solution1", type: "gives", strength: 10 },
          { from: "find_solutions", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_fund_03',
      problem: "x² + 5x = 0",
      problemType: "quadratic",
      difficulty: "fundamental",
      description: "Common factor extraction",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Missing constant term",
            value: "x² + 5x = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring",
            name: "Common Factor",
            type: "concepts",
            description: "Extract common factor x",
            value: "x(x + 5) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 0",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -5",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_common_factor",
            name: "Identify Common Factor",
            type: "steps",
            description: "Recognize that x is a common factor",
            value: "Step 1: x is common to both terms",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "extract_factor",
            name: "Extract Common Factor",
            type: "steps",
            description: "Factor out x from the equation",
            value: "Step 2: x(x + 5) = 0",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "set_factors_zero",
            name: "Set Factors to Zero",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 3: x = 0 or x + 5 = 0",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "solve_equations",
            name: "Solve Each Equation",
            type: "steps",
            description: "Solve for x in each equation",
            value: "Step 4: x = 0 or x = -5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "factoring", type: "solved by", strength: 9 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_common_factor", type: "requires", strength: 9 },
          { from: "identify_common_factor", to: "extract_factor", type: "enables", strength: 10 },
          { from: "extract_factor", to: "set_factors_zero", type: "leads to", strength: 10 },
          { from: "set_factors_zero", to: "solve_equations", type: "enables", strength: 10 },
          { from: "solve_equations", to: "solution1", type: "gives", strength: 10 },
          { from: "solve_equations", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_fund_04',
      problem: "x² - 9 = 0",
      problemType: "quadratic",
      difficulty: "fundamental",
      description: "Perfect square difference",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Perfect square difference",
            value: "x² - 9 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "difference_squares",
            name: "Difference of Squares",
            type: "concepts",
            description: "x² - 3² = (x+3)(x-3)",
            value: "(x+3)(x-3) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 3",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -3",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "recognize_pattern",
            name: "Recognize Pattern",
            type: "steps",
            description: "Identify difference of squares pattern",
            value: "Step 1: x² - 9 = x² - 3²",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_formula",
            name: "Apply Formula",
            type: "steps",
            description: "Use a² - b² = (a+b)(a-b)",
            value: "Step 2: (x+3)(x-3) = 0",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "set_factors_zero",
            name: "Set Factors to Zero",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 3: x+3=0 or x-3=0",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "solve_equations",
            name: "Solve Equations",
            type: "steps",
            description: "Solve for x in each equation",
            value: "Step 4: x = -3 or x = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "difference_squares", type: "solved by", strength: 10 },
          { from: "difference_squares", to: "solution1", type: "gives", strength: 10 },
          { from: "difference_squares", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "recognize_pattern", type: "requires", strength: 9 },
          { from: "recognize_pattern", to: "apply_formula", type: "enables", strength: 10 },
          { from: "apply_formula", to: "set_factors_zero", type: "leads to", strength: 10 },
          { from: "set_factors_zero", to: "solve_equations", type: "enables", strength: 10 },
          { from: "solve_equations", to: "solution1", type: "gives", strength: 10 },
          { from: "solve_equations", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_fund_05',
      problem: "x² + 6x + 9 = 0",
      problemType: "quadratic",
      difficulty: "fundamental",
      description: "Perfect square trinomial",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Perfect square trinomial",
            value: "x² + 6x + 9 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perfect_square",
            name: "Perfect Square",
            type: "concepts",
            description: "(x + 3)² = 0",
            value: "(x + 3)² = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Double Root",
            type: "values",
            description: "Repeated solution",
            value: "x = -3",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "identify_pattern",
            name: "Identify Pattern",
            type: "steps",
            description: "Recognize perfect square trinomial pattern",
            value: "Step 1: x² + 6x + 9 = (x + 3)²",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "write_factored_form",
            name: "Write Factored Form",
            type: "steps",
            description: "Express as perfect square",
            value: "Step 2: (x + 3)² = 0",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Take square root of both sides",
            value: "Step 3: x + 3 = 0",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "find_solution",
            name: "Find Solution",
            type: "steps",
            description: "Solve for x",
            value: "Step 4: x = -3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "perfect_square", type: "solved by", strength: 10 },
          { from: "perfect_square", to: "solution", type: "gives", strength: 10 },
          { from: "equation", to: "identify_pattern", type: "requires", strength: 9 },
          { from: "identify_pattern", to: "write_factored_form", type: "enables", strength: 10 },
          { from: "write_factored_form", to: "solve_equation", type: "leads to", strength: 10 },
          { from: "solve_equation", to: "find_solution", type: "enables", strength: 10 },
          { from: "find_solution", to: "solution", type: "gives", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'quad_inter_01',
      problem: "2x² + 7x + 3 = 0",
      problemType: "quadratic",
      difficulty: "intermediate",
      description: "Quadratic with leading coefficient ≠ 1",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Leading coefficient ≠ 1",
            value: "2x² + 7x + 3 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "quadratic_formula",
            name: "Quadratic Formula",
            type: "formulas",
            description: "x = (-b ± √(b²-4ac))/(2a)",
            value: "x = (-7 ± √(49-24))/4",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "discriminant",
            name: "Discriminant",
            type: "values",
            description: "b² - 4ac = 49 - 24 = 25",
            value: "Δ = 25",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = -3",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -0.5",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_coefficients",
            name: "Identify Coefficients",
            type: "steps",
            description: "Extract a, b, c from the equation",
            value: "Step 1: a=2, b=7, c=3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_discriminant",
            name: "Calculate Discriminant",
            type: "steps",
            description: "Compute b² - 4ac",
            value: "Step 2: Δ = 49 - 24 = 25",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_quadratic_formula",
            name: "Apply Quadratic Formula",
            type: "steps",
            description: "Use x = (-b ± √Δ) / (2a)",
            value: "Step 3: x = (-7 ± √25) / 4",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "simplify_results",
            name: "Simplify Results",
            type: "steps",
            description: "Calculate the two solutions",
            value: "Step 4: x = (-7 ± 5) / 4",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "quadratic_formula", type: "solved by", strength: 9 },
          { from: "quadratic_formula", to: "discriminant", type: "requires", strength: 8 },
          { from: "discriminant", to: "solution1", type: "enables", strength: 9 },
          { from: "discriminant", to: "solution2", type: "enables", strength: 9 },
          { from: "equation", to: "identify_coefficients", type: "requires", strength: 8 },
          { from: "identify_coefficients", to: "calculate_discriminant", type: "enables", strength: 9 },
          { from: "calculate_discriminant", to: "apply_quadratic_formula", type: "enables", strength: 10 },
          { from: "apply_quadratic_formula", to: "simplify_results", type: "leads to", strength: 10 },
          { from: "simplify_results", to: "solution1", type: "gives", strength: 10 },
          { from: "simplify_results", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_inter_02',
      problem: "x² - 5x + 6 = 0",
      problemType: "quadratic",
      difficulty: "intermediate",
      description: "Standard quadratic with factoring",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Standard form quadratic",
            value: "x² - 5x + 6 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring",
            name: "Factoring",
            type: "concepts",
            description: "Find factors that multiply to 6 and add to -5",
            value: "(x - 2)(x - 3) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 2",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = 3",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_coefficients",
            name: "Identify Coefficients",
            type: "steps",
            description: "Extract a, b, c from the equation",
            value: "Step 1: a=1, b=-5, c=6",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_factors",
            name: "Find Factors",
            type: "steps",
            description: "Find two numbers that multiply to 6 and add to -5",
            value: "Step 2: Factors are -2 and -3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "write_factored_form",
            name: "Write Factored Form",
            type: "steps",
            description: "Express as (x-2)(x-3) = 0",
            value: "Step 3: (x-2)(x-3) = 0",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "solve_linear_equations",
            name: "Solve Linear Equations",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 4: x-2=0 or x-3=0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "factoring", type: "solved by", strength: 9 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_coefficients", type: "requires", strength: 8 },
          { from: "identify_coefficients", to: "find_factors", type: "enables", strength: 9 },
          { from: "find_factors", to: "write_factored_form", type: "leads to", strength: 9 },
          { from: "write_factored_form", to: "solve_linear_equations", type: "enables", strength: 10 },
          { from: "solve_linear_equations", to: "solution1", type: "gives", strength: 10 },
          { from: "solve_linear_equations", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_inter_03',
      problem: "3x² - 12x + 9 = 0",
      problemType: "quadratic",
      difficulty: "intermediate",
      description: "Quadratic with common factor",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Has common factor 3",
            value: "3x² - 12x + 9 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_factor",
            name: "Common Factor",
            type: "concepts",
            description: "Factor out 3 first",
            value: "3(x² - 4x + 3) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "factoring",
            name: "Factoring",
            type: "concepts",
            description: "Factor the remaining quadratic",
            value: "3(x-1)(x-3) = 0",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 1",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = 3",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_common_factor",
            name: "Identify Common Factor",
            type: "steps",
            description: "Recognize that 3 is a common factor",
            value: "Step 1: 3 is common to all terms",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "extract_common_factor",
            name: "Extract Common Factor",
            type: "steps",
            description: "Factor out 3 from the equation",
            value: "Step 2: 3(x² - 4x + 3) = 0",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "factor_remaining",
            name: "Factor Remaining Quadratic",
            type: "steps",
            description: "Factor x² - 4x + 3",
            value: "Step 3: 3(x-1)(x-3) = 0",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "solve_equations",
            name: "Solve Each Factor",
            type: "steps",
            description: "Set each factor equal to zero",
            value: "Step 4: x-1=0 or x-3=0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "common_factor", type: "solved by", strength: 9 },
          { from: "common_factor", to: "factoring", type: "leads to", strength: 9 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_common_factor", type: "requires", strength: 9 },
          { from: "identify_common_factor", to: "extract_common_factor", type: "enables", strength: 10 },
          { from: "extract_common_factor", to: "factor_remaining", type: "leads to", strength: 9 },
          { from: "factor_remaining", to: "solve_equations", type: "enables", strength: 10 },
          { from: "solve_equations", to: "solution1", type: "gives", strength: 10 },
          { from: "solve_equations", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_inter_04',
      problem: "x² + 4x + 5 = 0",
      problemType: "quadratic",
      difficulty: "intermediate",
      description: "Quadratic with complex roots",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "No real roots",
            value: "x² + 4x + 5 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "discriminant",
            name: "Discriminant",
            type: "values",
            description: "b² - 4ac = 16 - 20 = -4",
            value: "Δ = -4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "complex_roots",
            name: "Complex Roots",
            type: "concepts",
            description: "Negative discriminant means complex roots",
            value: "x = -2 ± i",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First complex solution",
            value: "x₁ = -2 + i",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second complex solution",
            value: "x₂ = -2 - i",
            importance: 9,
            x: 120, y: 100, z: -15
          },
          {
            id: "identify_coefficients",
            name: "Identify Coefficients",
            type: "steps",
            description: "Extract a, b, c from the equation",
            value: "Step 1: a=1, b=4, c=5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_discriminant",
            name: "Calculate Discriminant",
            type: "steps",
            description: "Compute b² - 4ac",
            value: "Step 2: Δ = 16 - 20 = -4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "recognize_complex",
            name: "Recognize Complex Roots",
            type: "steps",
            description: "Negative discriminant indicates complex roots",
            value: "Step 3: Δ < 0, so roots are complex",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "apply_quadratic_formula",
            name: "Apply Quadratic Formula",
            type: "steps",
            description: "Use x = (-b ± √Δ) / (2a)",
            value: "Step 4: x = (-4 ± √(-4)) / 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "equation", to: "discriminant", type: "requires", strength: 9 },
          { from: "discriminant", to: "complex_roots", type: "indicates", strength: 9 },
          { from: "complex_roots", to: "solution1", type: "gives", strength: 10 },
          { from: "complex_roots", to: "solution2", type: "gives", strength: 10 },
          { from: "equation", to: "identify_coefficients", type: "requires", strength: 8 },
          { from: "identify_coefficients", to: "calculate_discriminant", type: "enables", strength: 9 },
          { from: "calculate_discriminant", to: "recognize_complex", type: "enables", strength: 10 },
          { from: "recognize_complex", to: "apply_quadratic_formula", type: "leads to", strength: 10 },
          { from: "apply_quadratic_formula", to: "solution1", type: "gives", strength: 10 },
          { from: "apply_quadratic_formula", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_inter_05',
      problem: "x² - 6x + 8 = 0",
      problemType: "quadratic",
      difficulty: "intermediate",
      description: "Standard quadratic with integer roots",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Standard form quadratic",
            value: "x² - 6x + 8 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring",
            name: "Factoring",
            type: "concepts",
            description: "Find factors that multiply to 8 and add to -6",
            value: "(x - 2)(x - 4) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 2",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = 4",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "factoring", type: "solved by", strength: 9 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'quad_hard_01',
      problem: "2x² + 5x - 3 = 0",
      problemType: "quadratic",
      difficulty: "harder",
      description: "Quadratic with irrational roots",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Leading coefficient ≠ 1",
            value: "2x² + 5x - 3 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "quadratic_formula",
            name: "Quadratic Formula",
            type: "formulas",
            description: "x = (-b ± √(b²-4ac))/(2a)",
            value: "x = (-5 ± √(25+24))/4",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "discriminant",
            name: "Discriminant",
            type: "values",
            description: "b² - 4ac = 25 + 24 = 49",
            value: "Δ = 49",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 0.5",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -3",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "quadratic_formula", type: "solved by", strength: 9 },
          { from: "quadratic_formula", to: "discriminant", type: "requires", strength: 8 },
          { from: "discriminant", to: "solution1", type: "enables", strength: 9 },
          { from: "discriminant", to: "solution2", type: "enables", strength: 9 }
        ]
      }
    },
    {
      id: 'quad_hard_02',
      problem: "x² - 2x - 15 = 0",
      problemType: "quadratic",
      difficulty: "harder",
      description: "Quadratic with larger numbers",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Standard form with larger coefficients",
            value: "x² - 2x - 15 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring",
            name: "Factoring",
            type: "concepts",
            description: "Find factors that multiply to -15 and add to -2",
            value: "(x - 5)(x + 3) = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 5",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -3",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "factoring", type: "solved by", strength: 9 },
          { from: "factoring", to: "solution1", type: "gives", strength: 10 },
          { from: "factoring", to: "solution2", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_hard_03',
      problem: "4x² - 12x + 9 = 0",
      problemType: "quadratic",
      difficulty: "harder",
      description: "Perfect square with leading coefficient",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Perfect square with a ≠ 1",
            value: "4x² - 12x + 9 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perfect_square",
            name: "Perfect Square",
            type: "concepts",
            description: "(2x - 3)² = 0",
            value: "(2x - 3)² = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Double Root",
            type: "values",
            description: "Repeated solution",
            value: "x = 1.5",
            importance: 9,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "perfect_square", type: "solved by", strength: 10 },
          { from: "perfect_square", to: "solution", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_hard_04',
      problem: "x² + 8x + 16 = 0",
      problemType: "quadratic",
      difficulty: "harder",
      description: "Perfect square trinomial",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Perfect square trinomial",
            value: "x² + 8x + 16 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perfect_square",
            name: "Perfect Square",
            type: "concepts",
            description: "(x + 4)² = 0",
            value: "(x + 4)² = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Double Root",
            type: "values",
            description: "Repeated solution",
            value: "x = -4",
            importance: 9,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "perfect_square", type: "solved by", strength: 10 },
          { from: "perfect_square", to: "solution", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'quad_hard_05',
      problem: "3x² + 7x - 6 = 0",
      problemType: "quadratic",
      difficulty: "harder",
      description: "Complex factoring with leading coefficient",
      graphData: {
        nodes: [
          {
            id: "equation",
            name: "Quadratic Equation",
            type: "concepts",
            description: "Complex factoring required",
            value: "3x² + 7x - 6 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "quadratic_formula",
            name: "Quadratic Formula",
            type: "formulas",
            description: "x = (-b ± √(b²-4ac))/(2a)",
            value: "x = (-7 ± √(49+72))/6",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "discriminant",
            name: "Discriminant",
            type: "values",
            description: "b² - 4ac = 49 + 72 = 121",
            value: "Δ = 121",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution1",
            name: "Root 1",
            type: "values",
            description: "First solution",
            value: "x₁ = 2/3",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "solution2",
            name: "Root 2",
            type: "values",
            description: "Second solution",
            value: "x₂ = -3",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "equation", to: "quadratic_formula", type: "solved by", strength: 9 },
          { from: "quadratic_formula", to: "discriminant", type: "requires", strength: 8 },
          { from: "discriminant", to: "solution1", type: "enables", strength: 9 },
          { from: "discriminant", to: "solution2", type: "enables", strength: 9 }
        ]
      }
    }
  ]
}; 